import { Injectable, Inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import * as moment from 'moment';

import { AddItineraryDto } from '@hiptraveler/data-access/api';
import { AuthState } from '@hiptraveler/data-access/auth';
import { UserState } from '@hiptraveler/data-access/user';
import { BrandState } from '@hiptraveler/data-access/brand';
import { SearchAction } from '@hiptraveler/data-access/search';
import { ItineraryAction, ItineraryState } from '@hiptraveler/data-access/itinerary';
import { ExperienceFinderStateService } from '@hiptraveler/features/experience-finder';
import { SnackbarService } from '@hiptraveler/snackbar';
import { FormFeatureService } from './form-feature.service';
import { PlanTripService } from '../plan-trip.service';
import { getRequestPayload } from './form-helper';
import * as Common from '@hiptraveler/common';

import { DataAccessService } from '../../../../modules/shell--search-page-root/filters/common';

@Injectable()
export class FormSubmitService {
  
  constructor(
    @Inject(Common.ENVIRONMENT) private environment: Common.Environment,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private location: Location,
    private store: Store,
    private appListener: Common.AppListenerService,
    private navbarControl: Common.NavbarControlStateService,
    private stateService: ExperienceFinderStateService,
    private searchLocation: Common.SearchLocationService,
    private searchPageControl: Common.SearchPageControlStateService,
    private snackbar: SnackbarService,
    private formFeature: FormFeatureService,
    private filtersDataAccess: DataAccessService,
    private planTrip: PlanTripService
  ) { }

  async submit({ placeResult }: { placeResult: Common.PlaceResult }): Promise<void> {

    if (this.validateFormData(placeResult)) return;

    this.stateService.processing$$.next(true);
    
    if (!Common.isWidget()) {
      sessionStorage.setItem(Common.addItineraryUXStateKey, '1');
      sessionStorage.setItem(Common.requestCancellationIgnoredEndpointsKey, JSON.stringify([ '/trip-planner/addItinerary' ]));

      /* To do: For improvement / This code block needs to be improved -- START */
      Common.getWindowRef()[Common.preventRequestCancellation] = '1';

      if (this.router.url.includes(`/${Common.currentLang()}/search`)) {
        await this.router.navigate([ '/',`${Common.currentLang()}`, 'thingstodo' ], { skipLocationChange: true });
      }

      this.searchLocation.emitPlaceResultDataToState(placeResult);

      await Common.promiseDelay(0);
      placeResult?.name && this.navigateOnSubmission(placeResult.name);

      try {
        const query = { q: placeResult?.formatted_address, locId: placeResult?.place_id };

        if (this.searchLocation.data?.locId !== query?.locId) {
          await firstValueFrom(this.store.dispatch(new SearchAction.GetLocationDataByQuery(query)));
        }
      } catch (error: any) {
        if (error?.name === 'EmptyError') return;
        console.log('@@@ Error (submit): ', error);
      } finally { } /* To do -- END */
    }

    if (this.formFeature.form.value.itinerary === 'skip') {
      return;
    }

    this.requestSnackbar();

    try {

      Common.isWidget() && this.runWidgetProcess({ processing: true });
      Common.isWidget() || this.stateService.overlayState$$.next(false);
      
      await firstValueFrom(this.store.dispatch(new ItineraryAction.PartialResetItineraryState([ 'basicInfo', 'actDateMap' ])));
      
      Common.isWidget() || this.navbarControl.setActiveNavbarButtons([]);
      
      Common.getWindowRef()[Common.addItineraryRequestPending] = '1';
      const authenticated = this.store.selectSnapshot(AuthState.authenticated);
      const requestPayload = getRequestPayload({ form: this.formFeature.form, stateService: this.stateService, placeResult, authenticated });
      await firstValueFrom(this.store.dispatch(new ItineraryAction.AddItinerary(requestPayload)));
      Common.getWindowRef()[Common.addItineraryRequestPending] = undefined;
      
      Common.getWindowRef()[Common.requestEndpointParamValue] = 'itinerary';
      
      Common.isWidget() || this.processRedirectByItineraryType(requestPayload);
      
      Common.isWidget() || this.navbarControl.navbarActionButtonState$$.next(true);
      
      this.runWidgetSuccessProcess(requestPayload);
      
      setTimeout(() => this.snackbar.dismiss(), 500);
    } catch (response: any) {
      await Common.promiseDelay(600);
      if (response.status === 403 || response.status === 409) {
        this.snackbar.open({ message: `snackbar.some_error`, duration: 5000 });
      } else if (response.error) {
        this.snackbar.open({ message: `snackbar.${response.error}`, duration: 5000 });
      } else {
        this.snackbar.open({ message: `snackbar.some_error`, duration: 5000 });
      }
      Common.isWidget() && this.runWidgetProcess({ processing: false });
    } finally {
      this.stateService.processing$$.next(false);
      Common.isWidget() && this.runWidgetProcess({ processing: false });
    }
  }

  private async navigateOnSubmission(placename: string): Promise<void> {

    const activities = this.stateService.getExperienceByCategory('activity');
    const experiences = this.stateService.getExperienceByCategory('experience');
    const travelStyle = this.stateService.getExperienceByCategory('travelStyle');

    const pathname = `/${Common.currentLang()}/search/${placename}`;

    let queries = '';
    activities.length && (queries += `?activities=${activities}`);
    experiences.length && (queries += `${queries.length ? '&' : '?'}experiences=${experiences}`);
    travelStyle.length && (queries += `${queries.length ? '&' : '?'}travelStyle=${travelStyle}`);

    let urlPath = '';
    const protocol = this.environment.local ? 'http' : 'https';
    const subdomain = this.environment.build === 'Production' ? '' : 'dev.';
    
    const startDate = moment(this.formFeature.form.value.startDate).format('MM-DD-YYYY');
    const endDate = moment(this.formFeature.form.value.endDate).format('MM-DD-YYYY');

    queries = (
      startDate && endDate 
      && (startDate !== 'Invalid date' || endDate !== 'Invalid date')
      && (startDate !== endDate)
    )
      ? `${queries}&checkInDate=${startDate}&checkOutDate=${endDate}`
      : queries;

    if (this.environment.build === 'Local') {
      const brand = this.store.selectSnapshot(BrandState.brandCampaign);
      const subdomain = brand?.cSubDomain ? `${brand.cSubDomain}.` : '';
      urlPath = `${protocol}://${subdomain}localhost:4200/${pathname.slice(1)}${queries}`;
    } else {
      urlPath = `${protocol}://${subdomain}hiptraveler.com/${pathname.slice(1)}${queries}`;
    }

    if (Common.isWidget()) {
      Common.getWindowRef()?.open(urlPath, '_blank');
      return;
    }

    await this.router.navigate([ pathname ]);
    await Common.promiseDelay(100);

    this.location.replaceState(`${pathname}${queries}`);
    await Common.promiseDelay(100);
    this.filtersDataAccess.forYouPage({ location: placename });
    setTimeout(() => this.appListener.emitGlobalSignal('exp-finder-query-listener'), 1000);

    Common.getWindowRef()[Common.preventRequestCancellation] = undefined;
  }

  private validateFormData(placeResult: Common.PlaceResult): boolean {

    if (this.formFeature.form.value.itinerary === 'skip') {
      if (placeResult?.name) {
        Common.isWidget() || this.searchLocation.emitPlaceResultDataToState(placeResult);
        this.navigateOnSubmission(placeResult.name);
      }
      return true;
    } 
    if (!this.formFeature.form.value.place || !placeResult?.name) {
      this.snackbar.open({ message: 'Please enter a location to continue.', duration: 5000 });
      return true;
    }
    if (!placeResult || !placeResult?.place_id) {
      this.snackbar.open({ message: 'Please select a location for your trip.', duration: 5000 });
      return true;
    }

    return false;
  }

  private runWidgetSuccessProcess(payload: AddItineraryDto): void {

    if (!Common.isWidget()) return;
    if (!this.store.selectSnapshot(UserState.itineraries)?.[0]) return;

    const fromUrl = `${Common.currentLang()}/itinerary`;
    const toUrl = `${Common.currentLang()}/compose/itinerary`;
    const urlPath = payload.customIti !== 'own' ? this.urlPath : this.urlPath.replace(fromUrl, toUrl)

    this.cdRef.detectChanges();
    const newWindow = Common.getWindowRef()?.open(urlPath, '_blank');

    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      console.log('@@@ ', 'Pop-up disabled!');
      this.planTrip.externalViewState$$.next(true);
      this.planTrip.itineraryRedirectUrl = urlPath;
    } else {
      console.log('@@@ ', 'Pop-up enabled!');
      this.stateService.dialogView$$.next('travel-style');
    }
  }

  private get urlPath(): string {

    const itinerary = this.store.selectSnapshot(UserState.itineraries)![0];

    let urlPath = `${Common.currentLang()}/${itinerary.isAutoGenerated ? 'itinerary' : 'compose/itinerary'}/${itinerary.pageTitle}`;

    const brand = this.store.selectSnapshot(BrandState.brandCampaign);

    if (Common.isBrandIframeWidget()) {
      const iframeLink = brand!.iframeLink!.replace( // For development only
        'http://localhost:8080/Hiptraveler/framewidget',
        `partners.hiptraveler.com/${Common.currentLang()}/trip-planner/framewidget/`
      );
      urlPath = `${iframeLink}#/${urlPath}`;
    } else {
      const protocol = this.environment.local ? 'http' : 'https';
      const subdomain = this.environment.build === 'Production' ? '' : 'dev.';
      urlPath = `${protocol}://${brand?.cSubDomain || `${subdomain}hiptraveler.com`}/${urlPath}`;
      urlPath = urlPath.replace('bahamas.localhost:8080/Hiptraveler', 'bahamas.localhost:4200'); // For development only
    }

    return urlPath;
  }

  private runWidgetProcess(value: { processing: boolean }): void {
    if (!Common.isWidget()) return;
    this.planTrip.processingViewState$$.next(value.processing)
    this.stateService.actionProcessing$$.next(value.processing);
  }

  private processRedirectByItineraryType(payload: AddItineraryDto): void {

    const pageTitle = this.store.selectSnapshot(ItineraryState.basicInfo)?.pageTitle;
    if (!pageTitle) return;

    const ignoredEndpoints = [ '/itineraryDetail/', '/getTripDetails/', '/getTaListbyIti/', '/setUserAction/' ];
    Common.updateIgnoredEndpoints(ignoredEndpoints);

    if (payload.customIti === 'own') {
      this.router.navigate([ `${Common.currentLang()}/compose/itinerary/${pageTitle}` ]);
    } 
    
    if (payload.customIti === 'auto') {
      this.router.navigate([ `${Common.currentLang()}/itinerary/${pageTitle}` ]);
    } 
    
    setTimeout(() => {
      this.snackbar.open({ message: 'Your Trip is loaded in Trip Planner!' });
      Common.removeIgnoredEndpoints(ignoredEndpoints);
    }, 600);
  }
  
  private requestSnackbar(): void {
    if (Common.isWidget()) return;
    const message = this.formFeature.form.value.itinerary === 'auto'
      ? 'We are building your personalized itinerary...'
      : 'Processing your itinerary...';
    setTimeout(() => this.snackbar.open({ message, duration: Infinity }), 500);
  }

}
